import { joinURL, QueryObject, withQuery } from "ufo";
import {
  Client,
  Enumerable,
  ListResult,
  Logger,
  OrieConfig,
  ResourceId,
  RetrieveResult,
} from "../types/internal";
import { wrap } from "../utils/data";
import { error } from "../utils/error";
import {
  BaseBusinessProfile,
  EventAvailability,
  ProductAvailability,
  QueryOptions,
} from "../models";

export const createClient = (config: OrieConfig, logger: Logger): Client => {
  if (!config.publishableKey) {
    error("Missing publishable key");
  }

  const cfg = {
    ...config,
    baseUrl: config.baseUrl || "https://commerce.orie.market/api",
    currency: config.currency?.toUpperCase() || "AUD",
    locale: config.locale || "en",
    user: config.user,
    maxRetries: config.maxRetries || 3,
  };

  const apiFetch = async <T>(
    method: string,
    url: string,
    body?: string
  ): Promise<T> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Publishable-Key": cfg.publishableKey!,
      "X-Locate": cfg.locale,
      "X-Currency": cfg.currency,
    };

    debug(`${method} ${url}`, { body });

    let attempt = 0;
    let lastError: any;

    while (attempt < cfg.maxRetries) {
      try {
        const res = await fetch(url, {
          method,
          headers,
          body,
        });

        if (!res.ok) {
          // Retry only on 5xx errors
          if (res.status >= 500) {
            throw new Error(`Server error (${res.status})`);
          }
          // For 4xx or other error, stop retrying
          const json = await res.json().catch(() => ({}));
          throw Object.assign(new Error("API Error"), {
            status: res.status,
            body: json,
          });
        }

        return res.json() as Promise<T>;
      } catch (err) {
        lastError = err;
        attempt++;

        if (attempt >= cfg.maxRetries) {
          break;
        }

        // Optional: Exponential backoff (100ms, 200ms, 400ms, ...)
        await new Promise((res) => setTimeout(res, 100 * 2 ** attempt));
      }
    }

    throw lastError;
  };

  const debug = (message: string, ...params: any[]) => {
    if (cfg.debug) {
      logger.debug(message, ...params);
    }
  };

  const make = (method: string) => {
    return async <T = any>(
      path: Enumerable<ResourceId>,
      options?: {
        data?: any;
        params?: QueryObject;
      }
    ) => {
      const p = wrap(path).map(String);
      const apiUrl = cfg.baseUrl + "/v1/sales";

      let url = joinURL(apiUrl, ...p);
      let data: string | undefined;

      if (options?.params) {
        url = withQuery(url, options.params);
      }

      if (options?.data) {
        data = JSON.stringify(options.data);
      }

      return apiFetch<T>(method, url, data);
    };
  };

  const get = make("GET");

  const fieldParams = (fields?: Enumerable<string>) => {
    const params: any = {};
    if (fields) {
      params.fields = wrap(fields).join(",");
    }
    return params;
  };

  return {
    get currency() {
      return cfg.currency;
    },
    get user() {
      return cfg.user;
    },
    get,
    post: make("POST"),
    put: make("PUT"),
    delete: async <T = any>(path: string) => {
      return apiFetch<T>(path, "DELETE");
    },
    retrieve: async <T>(
      path: Enumerable<ResourceId>,
      fields?: Enumerable<string>
    ): Promise<RetrieveResult<T>> => {
      const params = fieldParams(fields);
      return get(path, { params });
    },
    list: async <T>(
      path: Enumerable<ResourceId>,
      options?: QueryOptions
    ): Promise<ListResult<T>> => {
      const params = fieldParams(options?.fields);
      params.offset = options?.offset;
      params.limit = options?.limit;
      params.page = options?.page;
      params.search = options?.search;

      if (options?.order) {
        params.order = wrap(options.order).join(",");
      }

      return get(path, { params });
    },
    profiles: async <T>(
      type: "store" | "business" | "event" | "service",
      handle: string,
      options?: {
        fields?: Enumerable<string>;
        params?: QueryObject;
        with?: Enumerable<string>;
      }
    ) => {
      return get<T>(["profiles", type, handle], {
        params: {
          ...fieldParams(options?.fields),
          ...options?.params,
          with: wrap(options?.with).join(","),
        },
      });
    },
    availability: async <T extends ProductAvailability | EventAvailability>(
      type: "products" | "events",
      id: ResourceId
    ) => {
      return get<T>([type, id, "availability"]);
    },
    updateClient: (conf: Partial<OrieConfig>) => {
      const c = { ...cfg, ...conf };
      if (!c.publishableKey) {
        error("Missing publishable key");
      }
      Object.assign(cfg, c);
      return cfg;
    },
    urls: {
      carts(...urls: string[]) {
        return joinURL("carts", ...urls);
      },
      checkouts(...urls: string[]) {
        return joinURL("checkouts", ...urls);
      },
    },
  };
};
